import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { contactInfo } from "@/data/machines";
import { brand } from "@/data/brand";

const nav = [
  { label: "Inicio", href: "#inicio" },
  { label: "Maquinaria", href: "#catalogo" },
  { label: "Tarifas", href: "#tarifas" },
  { label: "Contacto", href: "#contacto" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-28 md:h-28 flex items-center justify-between">
        {/* Logo */}
        <a href="#inicio" data-testid="logo-link" className="group flex items-center gap-3">
          <div className="relative">
            <img
              src={brand.logoUrl}
              alt={`${contactInfo.companyName} logo`}
              className="h-12 w-12 sm:h-20 sm:w-20 md:h-24 md:w-24 rounded-xl object-cover shadow-md ring-1 ring-slate-200/80 transition-transform duration-300 group-hover:rotate-[6deg] group-hover:scale-105"
            />
            <span aria-hidden className="absolute -inset-0.5 rounded-xl ring-2 ring-sky-500/0 group-hover:ring-sky-500/25 transition duration-300 pointer-events-none" />
          </div>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-semibold text-[13px] uppercase tracking-[0.22em] text-slate-500">
              Maquinaria
            </span>
            <span className="font-heading font-bold tracking-tight text-slate-900 text-xl md:text-2xl -mt-0.5">
              Santacruz
            </span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-sky-500 hover:after:w-full after:transition-all"
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <a
            href={`tel:${contactInfo.phoneRaw}`}
            data-testid="header-call-button"
            className="group shine hidden sm:inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-4 md:px-5 py-2.5 text-sm transition-all duration-300 shadow-sm hover:shadow-md hover:shadow-sky-500/30 hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4 group-hover:wiggle" />
            Llamar ahora
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-lg text-slate-900 hover:bg-slate-100"
            aria-label="Menú"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-1">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="px-3 py-3 rounded-lg text-slate-800 font-medium hover:bg-slate-100"
              >
                {n.label}
              </a>
            ))}
            <a
              href={`tel:${contactInfo.phoneRaw}`}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-sky-500 text-white font-semibold rounded-xl px-4 py-3"
            >
              <Phone className="w-4 h-4" /> Llamar ahora
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
